import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Calendar, Layers, Users } from 'lucide-react'
import { useProjectStore, PROJECT_STATUSES, type ProjectStatus } from '../../store/projectStore'
import { usePermissions } from '../../hooks/usePermissions'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import ProjectFormModal from '../../components/projects/ProjectFormModal'

const STATUS_STYLES: Record<string, string> = {
  planning: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  on_hold: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
}

export default function ProjectsHubPage() {
  const { projects, isLoading, fetchProjects } = useProjectStore()
  const { can } = usePermissions()
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | ''>('')
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    fetchProjects(statusFilter ? { status: statusFilter } : {})
  }, [statusFilter, fetchProjects])

  const canCreate = can('pwa_project_monitoring', 'create') || can('admin_project_monitoring', 'create')

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Projects</h1>
        <p className="text-xs text-gray-500">Tap a project to see its board.</p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        <FilterChip active={statusFilter === ''} onClick={() => setStatusFilter('')}>All</FilterChip>
        {PROJECT_STATUSES.map((s) => (
          <FilterChip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)}>
            {s.replace('_', ' ')}
          </FilterChip>
        ))}
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : projects.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center">
          <Layers className="mx-auto h-10 w-10 text-gray-400" />
          <p className="mt-3 text-sm text-gray-500">No projects yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map((p) => (
            <Link
              key={p.id}
              to={`/projects/${p.id}`}
              className="block rounded-lg border border-gray-200 bg-white p-4 shadow-sm active:bg-gray-50"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: p.color || '#3b82f6' }} />
                  <h3 className="font-semibold text-gray-900 truncate">{p.name}</h3>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_STYLES[p.status]}`}>
                  {p.status.replace('_', ' ')}
                </span>
              </div>
              {p.description && (
                <p className="mt-2 text-sm text-gray-600 line-clamp-2">{p.description}</p>
              )}
              <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <Layers className="h-3.5 w-3.5" />
                  {p.task_count ?? 0}
                </div>
                <div className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {p.member_count ?? 0}
                </div>
                {p.end_date && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(p.end_date).toLocaleDateString()}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {canCreate && (
        <button
          onClick={() => setModalOpen(true)}
          className="fixed bottom-24 right-4 z-30 inline-flex items-center justify-center rounded-full bg-primary-600 p-4 text-white shadow-lg active:bg-primary-700"
          aria-label="New project"
        >
          <Plus className="h-6 w-6" />
        </button>
      )}

      <ProjectFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => fetchProjects(statusFilter ? { status: statusFilter } : {})}
      />
    </div>
  )
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${
        active ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
      }`}
    >
      {children}
    </button>
  )
}
